import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import {
  Button, Card, CardContent, CssBaseline, Grid, Icon, Fab, Typography, withStyles,
} from '@material-ui/core';
import { Field, reduxForm } from 'redux-form';
import {
  DEFAULT_STYLES, DRAWER_NOTIFICATION_WIDTH, ROUTE_NAME_DASHBOARD_USER, RXFORM_SPPB,
  RXFIELD_SPPB_DATE, RXFIELD_SPPB_DOC_CODE, RXFIELD_SPPB_DOC_TYPE, RXFIELD_SPPB_NO,
  RXFIELD_SPPB_NPWP, SPPB_DOC_TYPE_BC16,
  SPPB_DOC_TYPES,
} from '../../constant';
import { PageHeaderUser, LoadingIndicator } from '../../component';
import LocalizedString from '../../localization';
import {
  renderReduxFormTextField, renderReduxFormFilterableSelectAsyncField, renderReduxFormDateField,
} from '../../helper';
import { rxformValidateSppb } from '../../validation';
import { PickerItemShape } from '../../type';
import InfoDialog from './info-dialog';

const styles = theme => ({
  backIcon: {
    ...DEFAULT_STYLES.WEB_USER.BACK_ICON,
  },
  capitalize: {
    textTransform: 'capitalize',
  },
  content: {
    display: 'flex',
    justifyContent: 'center',
  },
  contentDrawerOpen: {
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${DRAWER_NOTIFICATION_WIDTH}px)`,
    },
  },
  contentDrawerClosed: {
    width: '100%',
  },
  pageNavigatorContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  pageTitle: {
    color: '#0266B4',
  },
  root: {
    width: '100%',
  },
  detailContent: {
    display: 'flex',
    flex: 1,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  spacer: {
    width: 20,
  },
  column: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

const SppbPage = ({
  classes, selectedDocType,
  dialogVisibility, notificationOpen, uploading,
  handleSubmit, onAppear, onClearPressed, onClosePressed, onSelectedDocTypeChange, onSubmitPressed,
  message,
}) => {
  useEffect(onAppear, []);

  return (
      <>
        <div className={classes.root}>
          <CssBaseline />

          <PageHeaderUser />

          <form onSubmit={handleSubmit(onSubmitPressed)}>
            <div
              className={
            classNames(classes.content, {
              [classes.contentDrawerOpen]: notificationOpen,
              [classes.contentDrawerClosed]: !notificationOpen,
            })
          }
              style={{ padding: '16px' }}
            >
              <Grid container spacing={24}>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <div className={classes.pageNavigatorContainer}>
                        <Fab
                          variant="round"
                          classes={{ root: classes.backIcon }}
                          component={Link}
                          to={ROUTE_NAME_DASHBOARD_USER}
                        >
                          <Icon>chevron_left</Icon>
                        </Fab>
                        <div style={{ marginLeft: '32px' }}>
                          <Typography variant="h5" className={classes.pageTitle}>{LocalizedString.sppb.title}</Typography>
                          <Typography variant="caption">{LocalizedString.sppb.caption}</Typography>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>

                <div
                  className={classNames(classes.detailContent)}
                  style={{ padding: '32px' }}
                >
                  <Grid container spacing={24}>
                    <Card className={classes.column}>
                      <CardContent className={classes.cardContent}>
                        <Field
                          name={RXFIELD_SPPB_DOC_TYPE}
                          component={renderReduxFormFilterableSelectAsyncField}
                          label={LocalizedString.sppb.labelDocType}
                          disabled={uploading}
                          onValueChange={onSelectedDocTypeChange}
                          options={SPPB_DOC_TYPES.map(x => ({ label: x, value: x }))}
                          required
                        />
                        <Field
                          name={RXFIELD_SPPB_NO}
                          component={renderReduxFormTextField}
                          label={LocalizedString.sppb.labelSppbNo}
                          disabled={uploading}
                          required
                        />
                        <Field
                          name={RXFIELD_SPPB_DATE}
                          component={renderReduxFormDateField}
                          label={LocalizedString.sppb.labelSppbDate}
                          disabled={uploading}
                          required
                        />
                        {selectedDocType && (
                        <Field
                          name={selectedDocType === SPPB_DOC_TYPE_BC16
                            ? RXFIELD_SPPB_DOC_CODE : RXFIELD_SPPB_NPWP}
                          component={renderReduxFormTextField}
                          label={selectedDocType === SPPB_DOC_TYPE_BC16
                            ? LocalizedString.sppb.labelDocCode : LocalizedString.sppb.labelNpwp}
                          disabled={selectedDocType === SPPB_DOC_TYPE_BC16 || uploading}
                          required
                        />
                        )}

                        <div
                          className={classes.row}
                          style={{ marginTop: '20px' }}
                        >
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={onClearPressed}
                          >
                            {LocalizedString.common.buttonCaptionClear}
                          </Button>
                          <div className={classes.spacer} />
                          <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                          >
                            {LocalizedString.common.buttonCaptionSubmit}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                </div>
              </Grid>
            </div>
          </form>
        </div>

        {uploading && <LoadingIndicator />}

        <InfoDialog
          visibility={dialogVisibility}
          message={message}
          onClosePressed={onClosePressed}
        />
      </>
  );
};

export default reduxForm({
  form: RXFORM_SPPB,
  enableReinitialize: true,
  validate: rxformValidateSppb,
})(withStyles(styles)(SppbPage));

SppbPage.propTypes = {
  classes: PropTypes.shape(PropTypes.any).isRequired,
  selectedDocType: PickerItemShape.isRequired,
  dialogVisibility: PropTypes.bool.isRequired,
  notificationOpen: PropTypes.bool.isRequired,
  uploading: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onAppear: PropTypes.func.isRequired,
  onClearPressed: PropTypes.func.isRequired,
  onClosePressed: PropTypes.func.isRequired,
  onSelectedDocTypeChange: PropTypes.func.isRequired,
  onSubmitPressed: PropTypes.func.isRequired,
  message: PropTypes.string.isRequired,
};
